import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { fileUrl, apiError, formatPrice } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Plus, Pencil, Trash2, Search, ImageOff, Upload, X, Check, RefreshCw } from "lucide-react";

const EMPTY = { name: "", sku: "", short_description: "", description: "", usage: "", price: "", old_price: "", category_id: "", image_url: null, images: [], label_pdf: "", available: true, stock: "", featured: false, active: true, is_demo: false, order: 0 };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const mainRef = useRef();
  const extraRef = useRef();

  const load = useCallback(() => api.get("/admin/products").then(({ data }) => setProducts(data)), []);
  useEffect(() => { load(); api.get("/categories", { params: { all: true } }).then(({ data }) => setCategories(data)); }, [load]);

  const [sync, setSync] = useState({ state: "idle", step: "", progress: 0 });
  const pollRef = useRef(null);
  const running = sync.state === "running";

  const pollStatus = useCallback(() => {
    api.get("/admin/catalog/status").then(({ data }) => {
      setSync(data);
      if (data.state === "done") {
        clearInterval(pollRef.current); pollRef.current = null;
        load(); api.get("/categories", { params: { all: true } }).then(({ data }) => setCategories(data));
        toast.success(`Catalogo aggiornato: ${data.result?.products ?? ""} prodotti · ${data.result?.added ?? 0} aggiunti · ${data.result?.updated ?? 0} aggiornati · ${data.result?.duplicates ?? 0} duplicati`);
      } else if (data.state === "error") {
        clearInterval(pollRef.current); pollRef.current = null;
        toast.error(`Aggiornamento fallito: ${data.error || "errore"}`);
      }
    }).catch(() => {});
  }, [load]);

  useEffect(() => {
    // riprendi il polling se un job è in corso al mount
    api.get("/admin/catalog/status").then(({ data }) => {
      setSync(data);
      if (data.state === "running" && !pollRef.current) pollRef.current = setInterval(pollStatus, 3000);
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pollStatus]);

  const startReimport = async () => {
    if (!window.confirm("Aggiornare il catalogo dalla sorgente? Prodotti e categorie verranno ricostruiti e le descrizioni ufficiali riscaricate. Le foto caricate manualmente sui prodotti verranno sostituite con quelle ufficiali.")) return;
    try {
      const { data } = await api.post("/admin/catalog/reimport");
      setSync({ state: "running", step: "Avvio…", progress: 2, ...data });
      if (!pollRef.current) pollRef.current = setInterval(pollStatus, 3000);
      toast.info("Aggiornamento avviato. Può richiedere 1-2 minuti.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: products.length + 1 }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, price: String(p.price), old_price: p.old_price ?? "", stock: p.stock ?? "", category_id: p.category_id || "", images: p.images || [] }); setOpen(true); };

  const doUpload = async (file, cb) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); cb(data.url); toast.success("Immagine caricata"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0,
      old_price: form.old_price === "" ? null : parseFloat(form.old_price),
      stock: form.stock === "" ? null : parseInt(form.stock),
      category_id: form.category_id || null };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      setOpen(false); load(); toast.success("Prodotto salvato");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Eliminare il prodotto?")) return; await api.delete(`/admin/products/${id}`); load(); toast.success("Eliminato"); };
  const toggle = async (p, field) => { await api.put(`/admin/products/${p.id}`, { ...p, [field]: !p[field], category_id: p.category_id || null }); load(); };

  const filtered = products.filter((p) => (p.name + p.sku).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold font-display text-slate-900">Gestione Prodotti</h1>
        <div className="flex items-center gap-2">
          <Button onClick={startReimport} disabled={running} variant="outline" className="rounded-full" data-testid="reimport-catalog-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${running ? "animate-spin" : ""}`} /> {running ? "Aggiornamento…" : "Aggiorna catalogo"}
          </Button>
          <Button onClick={openNew} className="rounded-full" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo Prodotto</Button>
        </div>
      </div>
      {running && (
        <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3" data-testid="reimport-progress">
          <div className="flex items-center justify-between text-sm text-slate-600"><span>{sync.step || "Aggiornamento in corso…"}</span><span>{sync.progress || 0}%</span></div>
          <div className="mt-2 h-2 rounded-full bg-emerald-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${sync.progress || 0}%` }} /></div>
        </div>
      )}
      <div className="relative max-w-sm mt-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Cerca prodotto..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 rounded-full" data-testid="admin-product-search" />
      </div>

      <Card className="mt-6 rounded-2xl border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Foto</TableHead><TableHead>Prodotto</TableHead><TableHead>Categoria</TableHead>
            <TableHead className="text-right">Prezzo</TableHead><TableHead className="text-center">Stock</TableHead>
            <TableHead className="text-center">Disp.</TableHead><TableHead className="text-center">Evidenza</TableHead>
            <TableHead className="text-center">Attivo</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} data-testid={`admin-product-${p.id}`}>
                <TableCell><div className="w-12 h-12 rounded-lg bg-emerald-50 overflow-hidden flex items-center justify-center">{fileUrl(p.image_url) ? <img src={fileUrl(p.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-5 h-5 text-emerald-200" />}</div></TableCell>
                <TableCell className="font-medium max-w-[180px] truncate">{p.name}{p.is_demo && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">DEMO</span>}</TableCell>
                <TableCell className="text-slate-500 text-sm">{p.category_name || "—"}</TableCell>
                <TableCell className="text-right font-medium">{formatPrice(p.price)}</TableCell>
                <TableCell className="text-center text-sm">{p.stock == null ? "∞" : p.stock}</TableCell>
                <TableCell className="text-center"><Switch checked={p.available} onCheckedChange={() => toggle(p, "available")} data-testid={`toggle-avail-${p.id}`} /></TableCell>
                <TableCell className="text-center"><Switch checked={p.featured} onCheckedChange={() => toggle(p, "featured")} data-testid={`toggle-feat-${p.id}`} /></TableCell>
                <TableCell className="text-center"><Switch checked={p.active} onCheckedChange={() => toggle(p, "active")} data-testid={`toggle-active-${p.id}`} /></TableCell>
                <TableCell><div className="flex justify-end gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 text-slate-400 hover:text-primary" data-testid={`edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="p-1.5 text-slate-400 hover:text-rose-500" data-testid={`del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div></TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Nessun prodotto.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Foto principale</Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-xl bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0">{fileUrl(form.image_url) ? <img src={fileUrl(form.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-6 h-6 text-emerald-200" />}</div>
                <div className="flex flex-col gap-2">
                  <input type="file" accept="image/*" ref={mainRef} onChange={(e) => e.target.files[0] && doUpload(e.target.files[0], (url) => setForm((f) => ({ ...f, image_url: url })))} className="hidden" data-testid="product-image-input" />
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => mainRef.current?.click()} disabled={uploading}><Upload className="w-4 h-4 mr-1" /> {uploading ? "..." : "Carica"}</Button>
                  {form.image_url && <Button type="button" variant="ghost" size="sm" className="text-rose-500" onClick={() => setForm({ ...form, image_url: null })}><X className="w-4 h-4 mr-1" /> Rimuovi</Button>}
                </div>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Immagini aggiuntive</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {form.images.map((im, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden">
                    <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} className="absolute top-0 right-0 bg-rose-500 text-white rounded-bl p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <input type="file" accept="image/*" ref={extraRef} onChange={(e) => e.target.files[0] && doUpload(e.target.files[0], (url) => setForm((f) => ({ ...f, images: [...f.images, url] })))} className="hidden" />
                <button type="button" onClick={() => extraRef.current?.click()} className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400"><Plus className="w-5 h-5" /></button>
              </div>
            </div>
            <div><Label className="mb-1.5 block">Nome prodotto</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block">SKU / Codice</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku" /></div>
              <div><Label className="mb-1.5 block">Categoria</Label>
                <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="product-category">
                  <option value="">Nessuna</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div><Label className="mb-1.5 block">Descrizione breve</Label><Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="product-short" /></div>
            <div><Label className="mb-1.5 block">Descrizione completa</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description" /></div>
            <div><Label className="mb-1.5 block">Modo di utilizzo</Label><Textarea rows={3} value={form.usage} onChange={(e) => setForm({ ...form, usage: e.target.value })} placeholder="Es. Sciogli 2 misurini in 250 ml di acqua..." data-testid="product-usage" /></div>
            <div><Label className="mb-1.5 block">Scheda PDF (ingredienti e valori nutrizionali)</Label><Input value={form.label_pdf || ""} onChange={(e) => setForm({ ...form, label_pdf: e.target.value })} placeholder="https://.../pl-XXXX-it.pdf" data-testid="product-label-pdf" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="mb-1.5 block">Prezzo (€)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price" /></div>
              <div><Label className="mb-1.5 block">Prezzo prec.</Label><Input type="number" step="0.01" value={form.old_price} onChange={(e) => setForm({ ...form, old_price: e.target.value })} data-testid="product-oldprice" /></div>
              <div><Label className="mb-1.5 block">Quantità</Label><Input type="number" placeholder="∞" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock" /></div>
            </div>
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="product-available" /> Disponibile</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-featured" /> In evidenza</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-active" /> Attivo</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="product-save"><Check className="w-4 h-4 mr-1" /> Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
